import { TaskAssignControl, TaskSourceChip } from "./CreateTaskDialog";
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API_BASE, formatApiError } from "./AuthContext";
import { toast } from "sonner";
import { Plus, Check, Trash2, CalendarClock, User as UserIcon } from "lucide-react";
import { logError } from "../lib/log";

/**
 * Reusable Tasks panel for use inside Lead/Client/Job drawers.
 * Props:
 *   - parentKind: 'lead' | 'client' | 'job'
 *   - parentId: id of the parent entity
 *   - title: optional heading override
 */
export default function TasksPanel({ parentKind, parentId, title }) {
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState("");
  const [newDue, setNewDue] = useState("");
  const [newAssignee, setNewAssignee] = useState("");
  const [newPriority, setNewPriority] = useState("medium");
  const [creating, setCreating] = useState(false);

  const fetchTasks = useCallback(async () => {
    if (!parentId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/tasks?parent_kind=${parentKind}&parent_id=${parentId}`);
      setTasks(r.data || []);
    } catch (e) {
      logError("tasks-panel:list", e);
    } finally { setLoading(false); }
  }, [parentKind, parentId]);

  useEffect(() => {
    let cancel = false;
    axios.get(`${API_BASE}/team/members`).then((r) => { if (!cancel) setMembers(r.data || []); }).catch(() => {});
    return () => { cancel = true; };
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const create = async (e) => {
    e?.preventDefault();
    if (!newTitle.trim()) {
      toast.error("Title is required");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API_BASE}/tasks`, {
        title: newTitle.trim(),
        due_at: newDue ? new Date(newDue).toISOString() : null,
        assignee_id: newAssignee || null,
        priority: newPriority,
        parent_kind: parentKind,
        parent_id: parentId,
      });
      setNewTitle(""); setNewDue("");
      await fetchTasks();
      window.dispatchEvent(new Event("tasks:refresh-pending"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add");
    } finally {
      setCreating(false);
    }
  };

  const onComplete = async (t) => {
    try {
      await axios.post(`${API_BASE}/tasks/${t.task_id}/complete`);
      await fetchTasks();
      window.dispatchEvent(new Event("tasks:refresh-pending"));
    } catch (e) {
      logError("tasks-panel:complete", e);
    }
  };

  const onReopen = async (t) => {
    try {
      await axios.post(`${API_BASE}/tasks/${t.task_id}/reopen`);
      await fetchTasks();
    } catch (e) { logError("tasks-panel:reopen", e); }
  };

  const onDelete = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try {
      await axios.delete(`${API_BASE}/tasks/${t.task_id}`);
      await fetchTasks();
    } catch (e) { logError("tasks-panel:delete", e); }
  };

  const formatDue = (s) => {
    if (!s) return "";
    try {
      const d = new Date(s);
      return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return s; }
  };

  const open = tasks.filter((t) => t.status === "open" && !t.parent_task_id);
  const done = tasks.filter((t) => t.status === "done" && !t.parent_task_id);
  // group sub-tasks by parent (v65 B1)
  const subsByParent = {};
  for (const t of tasks) {
    if (t.parent_task_id) {
      (subsByParent[t.parent_task_id] = subsByParent[t.parent_task_id] || []).push(t);
    }
  }
  const addSubTask = async (parentTaskId) => {
    const title = window.prompt("Sub-task title?");
    if (!title?.trim()) return;
    try {
      await axios.post(`${API_BASE}/tasks`, {
        title: title.trim(),
        parent_kind: parentKind, parent_id: parentId,
        parent_task_id: parentTaskId,
      });
      fetchTasks();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid={`tasks-panel-${parentKind}`}>
      <div className="overline mb-2 flex items-center justify-between">
        <span>{title || "Tasks"}</span>
        {open.length > 0 && (
          <span className="text-[var(--ink-3)] font-mono text-[10px]">
            {open.length} open{done.length > 0 ? ` · ${done.length} done` : ""}
          </span>
        )}
      </div>

      <form onSubmit={create} className="flex flex-wrap items-center gap-2 mb-3" data-testid={`tasks-panel-${parentKind}-form`}>
        <input
          type="text"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Add a task or follow-up…"
          className="flex-1 min-w-[200px] basis-full sm:basis-auto px-2 py-1.5 rounded border bg-transparent text-[13px]"
          style={{ borderColor: "var(--border)" }}
          data-testid={`tasks-panel-${parentKind}-input`}
        />
        <input
          type="datetime-local"
          value={newDue}
          onChange={(e) => setNewDue(e.target.value)}
          className="px-2 py-1.5 rounded border bg-transparent text-[12px]"
          style={{ borderColor: "var(--border)" }}
          data-testid={`tasks-panel-${parentKind}-due`}
        />
        <select
          value={newAssignee}
          onChange={(e) => setNewAssignee(e.target.value)}
          className="px-2 py-1.5 rounded border bg-transparent text-[12px]"
          style={{ borderColor: "var(--border)" }}
          data-testid={`tasks-panel-${parentKind}-assignee`}
        >
          <option value="">Unassigned</option>
          {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
        </select>
        <select
          value={newPriority}
          onChange={(e) => setNewPriority(e.target.value)}
          className="px-2 py-1.5 rounded border bg-transparent text-[12px]"
          style={{ borderColor: "var(--border)" }}
        >
          <option value="low">Low</option>
          <option value="medium">Med</option>
          <option value="high">High</option>
        </select>
        <button
          type="submit"
          disabled={creating || !newTitle.trim()}
          className="px-3 py-1.5 rounded text-[12px] font-medium disabled:opacity-50"
          style={{ background: "var(--ink)", color: "var(--paper)" }}
          data-testid={`tasks-panel-${parentKind}-add`}
        >
          <Plus size={12} className="inline mr-0.5" /> Add
        </button>
      </form>

      {loading ? (
        <div className="text-[var(--ink-3)] text-[12px]">Loading…</div>
      ) : open.length === 0 && done.length === 0 ? (
        <div className="text-[var(--ink-3)] text-[12px]">No tasks yet.</div>
      ) : (
        <div className="space-y-1" data-testid={`tasks-panel-${parentKind}-list`}>
          {open.map((t) => (
            <TaskItem key={t.task_id} task={t} subs={subsByParent[t.task_id] || []} members={members} onComplete={onComplete} onDelete={onDelete} onReopen={onReopen} formatDue={formatDue} onAddSubTask={addSubTask} onChanged={fetchTasks} />
          ))}
          {done.length > 0 && (
            <details className="mt-2">
              <summary className="text-[11px] text-[var(--ink-3)] cursor-pointer select-none px-1">
                {done.length} completed
              </summary>
              <div className="mt-1 space-y-1 opacity-70">
                {done.map((t) => (
                  <TaskItem key={t.task_id} task={t} subs={subsByParent[t.task_id] || []} members={members} onComplete={onComplete} onDelete={onDelete} onReopen={onReopen} formatDue={formatDue} onAddSubTask={addSubTask} onChanged={fetchTasks} />
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function TaskItem({ task, subs = [], members, onComplete, onDelete, onReopen, formatDue, onAddSubTask, onChanged }) {
  const done = task.status === "done";
  const isSub = !!task.parent_task_id;
  return (
    <div>
      <div
        className="flex items-start gap-2 px-2 py-1.5 rounded text-[13px]"
        style={{ background: "var(--subtle)" }}
        data-testid={`tasks-panel-row-${task.task_id}`}
      >
        <button
          type="button"
          onClick={() => (done ? onReopen(task) : onComplete(task))}
          title={done ? "Reopen" : "Mark done"}
          className="mt-0.5 w-4 h-4 rounded border flex items-center justify-center"
          style={{ borderColor: "var(--border)", background: done ? "var(--ink)" : "transparent", color: done ? "var(--paper)" : "var(--ink)" }}
          data-testid={`tasks-panel-toggle-${task.task_id}`}
        >
          {done && <Check size={10} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className={done ? "line-through text-[var(--ink-3)]" : "text-[var(--ink)]"}>{task.title}</div>
          <div className="flex items-center gap-2 text-[10px] text-[var(--ink-3)] mt-0.5">
            {task.due_at && (<span className="inline-flex items-center gap-0.5"><CalendarClock size={9} /> {formatDue(task.due_at)}</span>)}
            <TaskAssignControl task={task} onChanged={onChanged} testid={`tasks-panel-${task.task_id}`} />
            <TaskSourceChip task={task} />
            {task.priority !== "medium" && <span>· {task.priority}</span>}
            {subs.length > 0 && <span>· {subs.filter(s => s.status === "done").length}/{subs.length} sub-tasks</span>}
          </div>
        </div>
        {!isSub && onAddSubTask && !done && (
          <button type="button" onClick={() => onAddSubTask(task.task_id)}
                  className="text-[var(--ink-3)] hover:text-[var(--accent)] p-0.5"
                  title="Add sub-task"
                  data-testid={`tasks-panel-add-sub-${task.task_id}`}>
            <Plus size={11} />
          </button>
        )}
        <button
          type="button"
          onClick={() => onDelete(task)}
          className="text-[var(--ink-3)] hover:text-[var(--accent)] p-0.5"
          data-testid={`tasks-panel-delete-${task.task_id}`}
        >
          <Trash2 size={11} />
        </button>
      </div>
      {subs.length > 0 && (
        <div className="ml-6 mt-1 space-y-1 border-l border-[var(--border)] pl-2"
             data-testid={`tasks-panel-subs-${task.task_id}`}>
          {subs.map((st) => (
            <SubTaskRow key={st.task_id} task={st} members={members}
                       onComplete={onComplete} onDelete={onDelete}
                       onReopen={onReopen} formatDue={formatDue} />
          ))}
        </div>
      )}
    </div>
  );
}

function SubTaskRow({ task, members, onComplete, onDelete, onReopen, formatDue }) {
  const assignee = members.find((m) => m.user_id === task.assignee_id);
  const done = task.status === "done";
  return (
    <div
      className="flex items-start gap-2 px-2 py-1.5 rounded text-[12.5px]"
      style={{ background: "var(--subtle)" }}
      data-testid={`tasks-panel-row-${task.task_id}`}
    >
      <button
        type="button"
        onClick={() => (done ? onReopen(task) : onComplete(task))}
        title={done ? "Reopen" : "Mark done"}
        className="mt-0.5 w-4 h-4 rounded border flex items-center justify-center"
        style={{ borderColor: "var(--border)", background: done ? "var(--ink)" : "transparent", color: done ? "var(--paper)" : "var(--ink)" }}
        data-testid={`tasks-panel-toggle-${task.task_id}`}
      >
        {done && <Check size={10} />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={done ? "line-through text-[var(--ink-3)]" : "text-[var(--ink)]"}>{task.title}</div>
        <div className="flex items-center gap-2 text-[10px] text-[var(--ink-3)] mt-0.5">
          {task.due_at && (<span className="inline-flex items-center gap-0.5"><CalendarClock size={9} /> {formatDue(task.due_at)}</span>)}
          {assignee && (<span className="inline-flex items-center gap-0.5"><UserIcon size={9} /> {assignee.name}</span>)}
          {task.priority !== "medium" && <span>· {task.priority}</span>}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onDelete(task)}
        className="text-[var(--ink-3)] hover:text-[var(--accent)] p-0.5"
        data-testid={`tasks-panel-delete-${task.task_id}`}
      >
        <Trash2 size={11} />
      </button>
    </div>
  );
}
